/* Dr. Förster · Geschenk-Pop-up (DRF Gift 1.0)
 * Config comes from sections/drf-gift-promos.liquid (#drf-gift-config).
 * Watches every cart mutation (fetch + XHR + theme:cart:change), compares the unlocked gifts per promotion
 * with the gift lines already in the cart (line property _drf_gift = promotion id) and
 *  - opens the picker when a gift is still open,
 *  - removes surplus gift lines when the qualifying product leaves the cart.
 * Public API: window.DRFGift.open(), .check(), .config
 */
(() => {
  const el = document.getElementById('drf-gift-config');
  if (!el || window.DRFGift) return;
  let config;
  try { config = JSON.parse(el.textContent); } catch (e) { return; }
  if (!config || !Array.isArray(config.promos) || !config.promos.length) return;

  const L = config.labels || {};
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const CART_RE = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;
  const STORE = 'drf-gift:';
  let busy = 0;
  let timer = null;
  let state = [];
  let modal = null;
  let pill = null;
  let lastFocus = null;

  /* ---------- helpers ---------- */
  const today = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const live = (p) => (!p.start || p.start <= today()) && (!p.end || p.end >= today()) && p.gifts.length;
  const currency = () => (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
  const rate = () => parseFloat((window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || '1') || 1;
  const money = (cents) => {
    const lang = document.documentElement.lang || 'de';
    const whole = cents % 100 === 0;
    try {
      return new Intl.NumberFormat(lang, { style: 'currency', currency: currency(), minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 }).format(cents / 100);
    } catch (e) {
      return `${(cents / 100).toFixed(whole ? 0 : 2)} €`;
    }
  };
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const fill = (str, map) => String(str || '').replace(/\{(\w+)\}|\[(\w+)\]/g, (m, a, b) => {
    const k = a || b;
    return Object.prototype.hasOwnProperty.call(map, k) ? map[k] : m;
  });
  const getDismissed = (id) => {
    try { return parseInt(sessionStorage.getItem(STORE + id) || '0', 10) || 0; } catch (e) { return 0; }
  };
  const setDismissed = (id, n) => {
    try { sessionStorage.setItem(STORE + id, String(n)); } catch (e) { /* private mode */ }
  };
  const img = (src, w) => (src ? src.replace(/width=\d+/, `width=${w}`) : '');

  config.promos.forEach((p) => {
    p.triggerIds = (p.triggers || []).map((t) => Number(t.id));
    if (config.sort !== 'manual') p.gifts.sort((a, b) => b.price - a.price);
  });

  /* ---------- cart ---------- */
  async function api(path, body) {
    busy += 1;
    try {
      const res = await fetch(root + path, {
        method: body ? 'POST' : 'GET',
        headers: body ? { 'Content-Type': 'application/json', Accept: 'application/json' } : { Accept: 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
        credentials: 'same-origin',
      });
      if (!res.ok) throw new Error(`${path} ${res.status}`);
      return await res.json();
    } finally {
      busy -= 1;
    }
  }

  function analyse(cart) {
    const items = cart.items || [];
    return config.promos.filter(live).map((p) => {
      const giftLines = items.filter((i) => i.properties && i.properties._drf_gift === p.id);
      const giftQty = giftLines.reduce((n, i) => n + i.quantity, 0);
      const normal = items.filter((i) => !(i.properties && i.properties._drf_gift));
      let allowed = 0;
      let label = '';
      if (p.mode === 'subtotal') {
        const base = normal.reduce((n, i) => n + (i.final_line_price || 0), 0);
        if (p.min > 0 && base >= p.min * rate()) allowed = p.perTrigger;
      } else {
        const lines = normal.filter((i) => p.triggerIds.indexOf(Number(i.product_id)) > -1);
        allowed = lines.reduce((n, i) => n + i.quantity, 0) * p.perTrigger;
        label = lines.length ? lines[0].product_title : '';
      }
      return { promo: p, giftLines, giftQty, allowed, pending: Math.max(0, allowed - giftQty), label };
    });
  }

  async function trimSurplus(s) {
    let surplus = s.giftQty - s.allowed;
    if (surplus <= 0) return false;
    const lines = s.giftLines.slice().reverse();
    for (const line of lines) {
      if (surplus <= 0) break;
      const next = Math.max(0, line.quantity - surplus);
      surplus -= line.quantity - next;
      await api('cart/change.js', { id: line.key, quantity: next });
    }
    return true;
  }

  async function refreshTheme(openDrawer) {
    const cart = await api('cart.js');
    document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart }, bubbles: true }));
    // Pipeline re-renders the drawer items on theme:cart:reload (on the cart page it reloads the page).
    document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
    if (openDrawer) {
      const drawer = document.querySelector('[data-drawer="drawer-cart"]');
      if (drawer && !drawer.classList.contains('is-open') && !drawer.classList.contains('drawer--visible')) {
        const toggle = document.querySelector('[data-drawer-toggle="drawer-cart"]');
        if (toggle) toggle.click();
        else drawer.dispatchEvent(new CustomEvent('theme:drawer:open', { bubbles: true }));
      }
    }
    return cart;
  }

  async function check(reason) {
    if (busy) return;
    let cart;
    try { cart = await api('cart.js'); } catch (e) { return; }
    state = analyse(cart);
    let changed = false;
    for (const s of state) {
      if (s.giftQty > s.allowed) {
        try { changed = (await trimSurplus(s)) || changed; } catch (e) { /* ignore */ }
        s.giftQty = s.allowed;
        s.pending = 0;
      }
      if (s.allowed < getDismissed(s.promo.id)) setDismissed(s.promo.id, s.allowed);
    }
    if (changed) {
      try { await refreshTheme(false); } catch (e) { /* ignore */ }
    }
    const open = state.find((s) => s.pending > 0);
    const auto = open && (reason === 'mutation' || /\/cart\/?$/.test(location.pathname)) && open.allowed > getDismissed(open.promo.id);
    if (auto && !modal) show(open);
    else if (!modal) renderPill();
  }

  function schedule(reason) {
    clearTimeout(timer);
    timer = setTimeout(() => check(reason), reason === 'mutation' ? 450 : 150);
  }

  /* ---------- watch cart mutations ---------- */
  const isMutation = (url, method) => CART_RE.test(String(url || '')) && String(method || 'GET').toUpperCase() === 'POST';
  const origFetch = window.fetch;
  if (origFetch) {
    window.fetch = function drfGiftFetch(input, init) {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const method = (init && init.method) || (input && input.method) || 'GET';
      const promise = origFetch.apply(this, arguments);
      if (!busy && isMutation(url, method)) {
        promise.then((r) => { if (r && r.ok) schedule('mutation'); }).catch(() => {});
      }
      return promise;
    };
  }
  const XO = XMLHttpRequest.prototype.open;
  const XS = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function drfOpen(method, url) {
    this._drfGift = isMutation(url, method);
    return XO.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function drfSend() {
    if (this._drfGift && !busy) {
      this.addEventListener('load', () => { if (this.status >= 200 && this.status < 300) schedule('mutation'); });
    }
    return XS.apply(this, arguments);
  };
  // The theme may keep its own fetch reference, so its cart events count as mutations too
  // (except the initial cart sync the layout dispatches right after load).
  const bootAt = Date.now();
  document.addEventListener('theme:cart:change', () => {
    if (!busy) schedule(Date.now() - bootAt > 3000 ? 'mutation' : 'event');
  });

  /* ---------- modal ---------- */
  function close(dismiss) {
    if (!modal) return;
    const s = modal._state;
    if (dismiss && s) setDismissed(s.promo.id, s.allowed);
    modal.classList.remove('is-open');
    document.documentElement.classList.remove('drf-gift-lock');
    const m = modal;
    modal = null;
    setTimeout(() => m.remove(), 320);
    document.removeEventListener('keydown', onKey, true);
    if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    renderPill();
  }

  function onKey(e) {
    if (!modal) return;
    if (e.key === 'Escape') { e.preventDefault(); close(true); return; }
    if (e.key !== 'Tab') return;
    const f = modal.querySelectorAll('button:not([disabled]), a[href]');
    if (!f.length) return;
    const first = f[0];
    const last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function show(s) {
    if (modal) return;
    const p = s.promo;
    const max = p.gifts.reduce((m, g) => Math.max(m, g.price), 0);
    const vars = { bundle: esc(s.label || (p.triggers[0] && p.triggers[0].title) || ''), max: money(max), count: String(p.gifts.length), min: money(Math.round(p.min * rate())) };
    const step = s.giftQty + 1;
    const progress = s.allowed > 1 ? fill(L.progress, { n: step, total: s.allowed }) : '';
    lastFocus = document.activeElement;
    hidePill();

    modal = document.createElement('div');
    modal.className = 'drf-gift';
    modal._state = s;
    modal.innerHTML = `
      <div class="drf-gift__backdrop" data-drf-gift-close></div>
      <div class="drf-gift__panel" role="dialog" aria-modal="true" aria-labelledby="drf-gift-title">
        <button type="button" class="drf-gift__x" data-drf-gift-close aria-label="${esc(L.close || 'Close')}">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>
        </button>
        <header class="drf-gift__head">
          <div class="drf-gift__seal" aria-hidden="true"><span>+1</span><small>${esc(L.free || '')}</small></div>
          ${p.eyebrow ? `<p class="drf-gift__eyebrow">${esc(p.eyebrow)}${progress ? ` · ${esc(progress)}` : ''}</p>` : ''}
          <h2 id="drf-gift-title" class="drf-gift__title">${esc(p.heading)}</h2>
          ${p.text ? `<p class="drf-gift__text">${fill(esc(p.text), vars)}</p>` : ''}
        </header>
        <ul class="drf-gift__grid" role="listbox" aria-labelledby="drf-gift-title">
          ${p.gifts.map((g, i) => `
            <li style="--i:${i}">
              <button type="button" class="drf-gift__item" role="option" aria-selected="false" data-variant="${g.variant}" data-title="${esc(g.title)}">
                <span class="drf-gift__img">${g.image ? `<img src="${esc(img(g.image, 420))}" srcset="${esc(img(g.image, 260))} 260w, ${esc(img(g.image, 420))} 420w" sizes="(max-width: 767px) 44vw, 200px" alt="" loading="lazy" width="420" height="420">` : ''}</span>
                <span class="drf-gift__check" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg></span>
                <span class="drf-gift__name">${esc(g.title)}</span>
                <span class="drf-gift__price"><s>${esc(L.value || '')} ${money(g.price)}</s><strong>${esc(L.free || '')}</strong></span>
              </button>
            </li>`).join('')}
        </ul>
        <footer class="drf-gift__foot">
          ${p.note ? `<p class="drf-gift__note">${esc(p.note)}</p>` : ''}
          <p class="drf-gift__error" role="alert" hidden>${esc(L.error || '')}</p>
          <button type="button" class="drf-gift__cta" data-drf-gift-add disabled>${esc(L.ctaPick || '')}</button>
          <button type="button" class="drf-gift__later" data-drf-gift-close>${esc(L.later || '')}</button>
        </footer>
      </div>`;
    document.body.appendChild(modal);
    document.documentElement.classList.add('drf-gift-lock');
    void modal.offsetHeight; // commit the start state so the open transition runs
    modal.classList.add('is-open');

    const cta = modal.querySelector('[data-drf-gift-add]');
    let chosen = null;
    modal.addEventListener('click', (e) => {
      if (e.target.closest('[data-drf-gift-close]')) { close(true); return; }
      const item = e.target.closest('.drf-gift__item');
      if (item) {
        modal.querySelectorAll('.drf-gift__item').forEach((b) => b.setAttribute('aria-selected', b === item ? 'true' : 'false'));
        chosen = { variant: Number(item.dataset.variant), title: item.dataset.title };
        cta.disabled = false;
        cta.innerHTML = fill(esc(L.cta || ''), { gift: `<span>${esc(chosen.title)}</span>` });
        return;
      }
      if (e.target.closest('[data-drf-gift-add]') && chosen) add(s, chosen, cta);
    });
    document.addEventListener('keydown', onKey, true);
    setTimeout(() => { const first = modal && modal.querySelector('.drf-gift__item'); if (first) first.focus({ preventScroll: true }); }, 60);
    track('drf_gift_open', { promo: p.id });
  }

  async function add(s, chosen, cta) {
    const p = s.promo;
    const err = modal.querySelector('.drf-gift__error');
    err.hidden = true;
    cta.disabled = true;
    cta.classList.add('is-loading');
    const props = { _drf_gift: p.id };
    if (L.property) props[L.property] = s.label ? `${L.free} · ${s.label}` : L.free;
    try {
      await api('cart/add.js', { items: [{ id: chosen.variant, quantity: 1, properties: props }] });
      track('drf_gift_add', { promo: p.id, variant: chosen.variant });
      const more = s.allowed - (s.giftQty + 1);
      close(false);
      await refreshTheme(true);
      toast(L.added);
      if (more > 0) setTimeout(() => check('mutation'), 900);
    } catch (e) {
      cta.disabled = false;
      cta.classList.remove('is-loading');
      err.hidden = false;
    }
  }

  /* ---------- toast + reminder ---------- */
  function toast(text) {
    if (!text) return;
    const t = document.createElement('div');
    t.className = 'drf-gift-toast';
    t.setAttribute('role', 'status');
    t.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg><span>${esc(text)}</span>`;
    document.body.appendChild(t);
    requestAnimationFrame(() => requestAnimationFrame(() => t.classList.add('is-in')));
    setTimeout(() => { t.classList.remove('is-in'); setTimeout(() => t.remove(), 400); }, 3200);
  }

  function hidePill() {
    if (pill) pill.classList.remove('is-in');
    document.querySelectorAll('.drf-gift-banner').forEach((b) => { b.hidden = true; });
  }

  // Where the inline reminder goes: cart drawer (above subtotal), cart page, bundle buy box, custom slots.
  const SLOTS = [
    ['[data-drawer="drawer-cart"] .drawer__bottom', 'prepend'],
    ['.template-cart main [data-cart-form]', 'prepend'],
    ['.drf-b-buy', 'append'],
    ['[data-drf-gift-slot]', 'append'],
  ];

  function bannerHtml(s) {
    const max = s.promo.gifts.reduce((m, g) => Math.max(m, g.price), 0);
    const sub = fill(esc(L.reminderSub || ''), { max: money(max), count: String(s.promo.gifts.length) });
    return `<span class="drf-gift-banner__seal" aria-hidden="true">+1</span>
      <span class="drf-gift-banner__copy"><strong>${esc(L.reminder || '')}</strong>${sub ? `<small>${sub}</small>` : ''}</span>
      <button type="button" class="drf-gift-banner__cta" data-drf-gift-open>${esc(L.reminderCta || '')}</button>`;
  }

  function renderBanners(s) {
    SLOTS.forEach(([sel, where]) => {
      document.querySelectorAll(sel).forEach((host) => {
        let b = host.querySelector(':scope > .drf-gift-banner');
        if (!s) { if (b) b.hidden = true; return; }
        if (!b) {
          b = document.createElement('div');
          b.className = 'drf-gift-banner';
          if (where === 'prepend') host.prepend(b); else host.append(b);
        }
        b.innerHTML = bannerHtml(s);
        b.hidden = false;
      });
    });
  }

  function renderPill() {
    const s = state.find((x) => x.pending > 0);
    document.querySelectorAll('[data-drf-gift-open]:not(.drf-gift-banner__cta)').forEach((b) => { b.hidden = !s; });
    if (!s || !L.reminder) { hidePill(); return; }
    renderBanners(s);
    if (!pill) {
      pill = document.createElement('div');
      pill.className = 'drf-gift-pill';
      pill.innerHTML = `<span class="drf-gift-pill__icon" aria-hidden="true">+1</span><span class="drf-gift-pill__text"></span><button type="button" class="drf-gift-pill__cta" data-drf-gift-open></button>`;
      document.body.appendChild(pill);
    }
    pill.classList.toggle('has-preview-bar', !!document.getElementById('PBarNextFrameWrapper'));
    pill.querySelector('.drf-gift-pill__text').textContent = L.reminder;
    pill.querySelector('.drf-gift-pill__cta').textContent = L.reminderCta || '';
    void pill.offsetHeight;
    pill.classList.add('is-in');
  }

  function track(name, data) {
    try { if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) window.Shopify.analytics.publish(name, data); } catch (e) { /* ignore */ }
  }

  async function open() {
    if (modal) return;
    let cart;
    try { cart = await api('cart.js'); } catch (e) { return; }
    state = analyse(cart);
    const s = state.find((x) => x.pending > 0);
    if (s) show(s);
  }

  document.addEventListener('click', (e) => {
    const t = e.target.closest && e.target.closest('[data-drf-gift-open]');
    if (!t) return;
    e.preventDefault();
    open();
  });

  window.DRFGift = { config, open, check: () => check('manual') };
  schedule('load');
})();
